import axios from "axios";
import Cookies from "js-cookie";
import { checkIfInIframe } from "./checkIfInIframe";

const isInIframe = checkIfInIframe();

const BASE_URL = isInIframe
	? import.meta.env.VITE_IFRAME_API_URL
	: import.meta.env.VITE_API_URL;

const COOKIE_POLL_ATTEMPTS = 3;
const COOKIE_POLL_INTERVAL_MS = 50;

// Shared in-flight request - every caller (RTK Query, axios) awaits the same
// promise instead of firing its own /sanctum/csrf-cookie request.
let inFlightRequest = null;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The Set-Cookie header from csrf-cookie isn't guaranteed to be visible via
// document.cookie in the same tick it resolves in - poll briefly instead of
// assuming it's already there.
const waitForXsrfCookie = async () => {
	for (let attempt = 1; attempt <= COOKIE_POLL_ATTEMPTS; attempt++) {
		if (Cookies.get("XSRF-TOKEN")) {
			return true;
		}
		if (attempt < COOKIE_POLL_ATTEMPTS) {
			await wait(COOKIE_POLL_INTERVAL_MS);
		}
	}
	return false;
};

const requestCsrfCookie = () => {
	inFlightRequest = axios
		.get(`${BASE_URL}/sanctum/csrf-cookie`, {
			withCredentials: true,
			withXSRFToken: true,
		})
		.then(async () => {
			const found = await waitForXsrfCookie();
			if (!found) {
				throw new Error(
					"XSRF-TOKEN cookie still missing after /sanctum/csrf-cookie resolved",
				);
			}
		})
		.catch((err) => {
			console.error("[ensureCsrfCookie] Failed to initialize CSRF cookie:", err);
			throw err;
		})
		.finally(() => {
			inFlightRequest = null;
		});

	return inFlightRequest;
};

// Ensures the XSRF-TOKEN cookie exists, sharing a single awaited request
// across every concurrent caller. Resolves immediately if the cookie is
// already present.
const ensureCsrfCookie = () => {
	if (Cookies.get("XSRF-TOKEN")) {
		return Promise.resolve();
	}

	return inFlightRequest || requestCsrfCookie();
};

// Forces a fresh csrf-cookie fetch (e.g. after a 419) even if a - now stale -
// XSRF-TOKEN cookie is still present.
export const refreshCsrfCookie = () => {
	return inFlightRequest || requestCsrfCookie();
};

export default ensureCsrfCookie;
